import React, { useState, useEffect, useCallback } from "react";
import { Loader2Icon, ChevronUp, ChevronDown, UsersIcon } from "lucide-react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";

import { fetchAllComments, addComment, Comment } from "../../lib/commentLesson";
import { formatRelativeTime } from "../../utils/timeComment";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

// PERUBAHAN 1: Tambahkan prop `onLoadComplete` ke dalam interface
// Prop ini bersifat opsional (?) agar komponen tetap bisa digunakan di tempat lain
interface CommentSectionProps {
  lessonPlanId: string;
  onLoadComplete?: () => void;
}

const CommentItem: React.FC<{ comment: Comment; currentUserId: string }> = ({
  comment,
  currentUserId,
}) => {
  // ... (Tidak ada perubahan di sini)
  const isOwnComment = comment.user_id === currentUserId;
  const bgColor = isOwnComment ? "bg-[#FEEFFE]" : "bg-white border-gray-200";
  const textColor = "text-[#45434C]";
  const nameColor = "text-[#37363E] font-semibold";

  const profile = comment.user_profiles || {
    name: "Guru Anonim",
    avatar_url: "",
  };

  return (
    <div
      className={`p-4 rounded-xl shadow-sm transition-colors duration-200 ${bgColor} border`}
    >
      <div className="flex items-start space-x-3">
        {profile.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt={profile.name}
            className="w-10 h-10 rounded-full object-cover bg-gray-300 flex-shrink-0"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gray-300 flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          <span className={`block text-sm ${nameColor}`}>
            {isOwnComment ? "Anda" : profile.name || "Guru Anonim"}
          </span>
          <span className="block text-xs text-gray-500 mb-1">
            {formatRelativeTime(comment.created_at)}
          </span>
          <p className={`text-sm ${textColor} whitespace-pre-wrap`}>
            {comment.content}
          </p>
        </div>
      </div>
    </div>
  );
};

// PERUBAHAN 2: Terima prop `onLoadComplete` di sini
export const CommentSection: React.FC<CommentSectionProps> = ({
  lessonPlanId,
  onLoadComplete,
}) => {
  const { user } = useAuth();
  const currentUserId = user?.id || "";

  const [allComments, setAllComments] = useState<Comment[]>([]);
  const [visibleComments, setVisibleComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [totalComments, setTotalComments] = useState<number>(0);

  const loadAllComments = useCallback(async () => {
    if (!lessonPlanId) return;
    setIsLoading(true);
    try {
      const fetchedComments = await fetchAllComments(lessonPlanId);
      setAllComments(fetchedComments);
      setTotalComments(fetchedComments.length);
    } catch {
      toast.error("Gagal memuat komentar.");
    } finally {
      setIsLoading(false);
      // PERUBAHAN 3: Panggil `onLoadComplete` setelah loading selesai
      // Kita cek dulu apakah prop-nya ada sebelum memanggilnya
      if (onLoadComplete) {
        onLoadComplete();
      }
    }
    // PERUBAHAN 4: Tambahkan `onLoadComplete` ke dependency array
  }, [lessonPlanId, onLoadComplete]);

  // useEffect untuk mengatur komentar yang terlihat
  useEffect(() => {
    if (showAll) {
      setVisibleComments(allComments);
    } else {
      setVisibleComments(allComments.slice(0, 3));
    }
  }, [allComments, showAll]);

  // Loading awal
  useEffect(() => {
    loadAllComments();
  }, [loadAllComments]);

  // Realtime listener Supabase
  useEffect(() => {
    if (!lessonPlanId) return;

    const channel = supabase
      .channel("comments-lesson-channel")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "comments_lesson",
          filter: `lesson_plan_id=eq.${lessonPlanId}`,
        },
        async (_payload) => {
          await loadAllComments();
          toast.success("Komentar baru masuk!");
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [lessonPlanId, loadAllComments]);

  const handlePostComment = async () => {
    const trimmed = newComment.trim();
    if (!trimmed || !lessonPlanId || !currentUserId) {
      toast.error("Komentar tidak boleh kosong atau pengguna belum login.");
      return;
    }

    setIsPosting(true);
    try {
      await addComment(lessonPlanId, currentUserId, trimmed);
      setNewComment("");
      toast.success("Komentar berhasil diposting!");
      // Komentar baru akan otomatis di-fetch ulang oleh realtime listener
    } catch {
      toast.error("Gagal memposting komentar.");
    } finally {
      setIsPosting(false);
    }
  };

  // ✅ Logika tombol "Lihat Lainnya" (sama persis)
  const isShowMoreVisible = totalComments > 3 && !showAll;

  if (!lessonPlanId) return null;

  return (
    <div className="w-full mt-8 pt-6 border-t border-gray-200">
      <h2 className="text-xl font-semibold text-[#373e46] mb-4 flex items-center gap-2">
        <UsersIcon className="w-4 h-4" />
        Live Collaboration
      </h2>

      <div className="mb-6 p-4 border border-gray-200 bg-[#F5F1FF] rounded-xl shadow-sm">
        <div className="flex space-x-3">
          {user?.profile?.avatar_url ? (
            <img
              src={user.profile.avatar_url}
              alt={user.name}
              className="w-10 h-10 rounded-full object-cover bg-gray-300 flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-300 flex-shrink-0" />
          )}
          <div className="flex-1 flex flex-col gap-2">
            <Textarea
              placeholder="Berikan pendapat tentang materi ini..."
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              disabled={isPosting || !user}
              rows={3}
              className="rounded-xl border-none focus:ring-8 focus:ring-[#6b2cfe] transition-colors resize-none bg-[#F5F1FF]"
            />
            <Button
              onClick={handlePostComment}
              disabled={isPosting || !newComment.trim() || !user}
              className="self-end px-4 py-2 bg-[#6b2cfe] hover:bg-[#5a24cc] text-white rounded-lg transition-all duration-300 h-9"
            >
              {isPosting ? (
                <Loader2Icon className="w-4 h-4 animate-spin" />
              ) : (
                "Post"
              )}
            </Button>
            {!user && (
              <p className="text-xs text-red-500 mt-1">
                Anda harus login untuk berkomentar.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {isLoading && (
          <div className="flex justify-center items-center h-20">
            <Loader2Icon className="w-6 h-6 animate-spin text-[#6b2cfe]" />
            <span className="ml-3 text-gray-500">Memuat komentar...</span>
          </div>
        )}

        {!isLoading && allComments.length === 0 && (
          <div className="text-center p-8 bg-gray-50 rounded-xl text-gray-500">
            Belum ada komentar untuk materi ini.
          </div>
        )}

        {/* ✅ Menggunakan visibleComments, bukan comments */}
        {!isLoading &&
          visibleComments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUserId={currentUserId}
            />
          ))}
      </div>

      {isShowMoreVisible && (
        <div className="flex justify-center mt-6">
          <Button
            onClick={() => setShowAll(true)}
            variant="outline"
            className="flex items-center gap-2 text-[#6b2cfe] border-[#6b2cfe] hover:bg-[#6b2cfe] hover:text-white transition-all duration-300 rounded-xl px-6 py-2"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            Lihat {totalComments - visibleComments.length} Komentar Lainnya
          </Button>
        </div>
      )}

      {showAll && totalComments > 3 && (
        <div className="flex justify-center mt-6">
          <Button
            onClick={() => setShowAll(false)}
            variant="outline"
            className="flex items-center gap-2 text-gray-500 border-gray-300 hover:bg-gray-50 transition-all duration-300 rounded-xl px-6 py-2"
          >
            <ChevronUp className="w-4 h-4" />
            Sembunyikan
          </Button>
        </div>
      )}
    </div>
  );
};
